import type { UnitCobranza } from '@yawi/types';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { formatCurrency, formatDate, PAYMENT_STATUS_META } from '../utils/format';

interface UnitRowProps {
  unit: UnitCobranza;
  selected: boolean;
  pending: boolean;
  onToggleSelect: (id: string) => void;
  onToggleAccess: (unit: UnitCobranza) => void;
}

const AVATAR_STACK_LIMIT = 5;

export const UnitRow: React.FC<UnitRowProps> = ({
  unit,
  selected,
  pending,
  onToggleSelect,
  onToggleAccess,
}) => {
  const [expanded, setExpanded] = useState(false);
  const status = PAYMENT_STATUS_META[unit.paymentStatus];
  const count = unit.residents.length;
  const stack = unit.residents.slice(0, AVATAR_STACK_LIMIT);
  const overflow = count - stack.length;

  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
      <td className="px-4 py-4 align-top">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(unit.id)}
          aria-label={`Seleccionar unidad ${unit.unit}`}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#7C5CFC] focus:ring-[#7C5CFC]"
        />
      </td>
      <td className="px-4 py-4 align-top">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Ocultar' : 'Mostrar'} residentes de ${unit.unit}`}
          className="flex w-full cursor-pointer items-center gap-2 text-left"
        >
          <span className="text-slate-400">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-800">{unit.unit}</span>
            <span className="mt-1 flex items-center gap-2">
              <span className="flex -space-x-2">
                {stack.map((r) => (
                  <span
                    key={r.id}
                    title={r.name}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold text-white ring-2 ring-white"
                    style={{ backgroundColor: r.avatarColor }}
                  >
                    {r.initials}
                  </span>
                ))}
                {overflow > 0 && (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[9px] font-semibold text-slate-500 ring-2 ring-white">
                    +{overflow}
                  </span>
                )}
              </span>
              <span className="text-xs text-slate-400">
                {count} residente{count === 1 ? '' : 's'}
              </span>
            </span>
          </span>
        </button>

        {expanded && (
          <ul className="mt-3 space-y-2 pl-6">
            {unit.residents.map((r) => (
              <li key={r.id} className="flex items-center gap-2">
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                  style={{ backgroundColor: r.avatarColor }}
                >
                  {r.initials}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-slate-700">{r.name}</span>
                  <span className="block truncate text-xs text-slate-400">{r.email}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${status.badge}`}>
          {status.label}
        </span>
      </td>
      <td className="px-4 py-4 text-right align-top">
        <p className={`text-sm font-semibold ${unit.debtAmount > 0 ? 'text-slate-800' : 'text-slate-400'}`}>
          {unit.debtAmount > 0 ? formatCurrency(unit.debtAmount) : '—'}
        </p>
        <p className="text-xs text-slate-400">Últ. pago {formatDate(unit.lastPaymentDate)}</p>
      </td>
      <td className="px-4 py-4 align-top">
        <div className="flex items-center justify-end gap-2">
          <span
            className={`text-xs font-medium ${unit.isBlocked ? 'text-red-600' : 'text-emerald-600'}`}
          >
            {unit.isBlocked ? 'Bloqueado' : 'Activo'}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={!unit.isBlocked}
            aria-label={`${unit.isBlocked ? 'Habilitar' : 'Bloquear'} acceso de la unidad ${unit.unit}`}
            disabled={pending}
            onClick={() => onToggleAccess(unit)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:opacity-60 ${
              unit.isBlocked ? 'bg-red-500' : 'bg-emerald-500'
            }`}
          >
            {pending ? (
              <Loader2 size={14} className="absolute left-1/2 -translate-x-1/2 animate-spin text-white" />
            ) : (
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  unit.isBlocked ? 'translate-x-1' : 'translate-x-6'
                }`}
              />
            )}
          </button>
        </div>
      </td>
    </tr>
  );
};
